/**
 * FinanceSidebar — Zone-aware minimal sidebar for Finance module.
 *
 * Shows only navigation items relevant to the current zone:
 * - Reports zone: 8 report items
 * - Entry zone: Transactions, Allocations
 * - Queues zone: Collections, Payments Due, Cheques
 * - Setup zone: Accounts, Subledgers, Settings
 *
 * Logo click returns to Start page (Finance Menu).
 */

import { Link, useLocation } from 'react-router-dom'
import {
  BarChart3,
  FileText,
  TrendingUp,
  ClipboardList,
  BookOpen,
  Wallet,
  Landmark,
  Receipt,
  ArrowRightLeft,
  Link2,
  HandCoins,
  CreditCard,
  UserCheck,
  Settings2,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Calendar,
  History,
  GitBranch,
  Zap,
  CalendarCheck,
  CalendarDays,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/stores/ui-store'
import { useUserPermissions } from '@/hooks/useHasPermission'
import {
  detectZone,
  ZONE_NAV_ITEMS,
  filterZoneItemsByPermissions,
  type FinanceZone,
} from '../utils/zones'

// Icon mapping for nav items
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  '/finance/reports': BarChart3,
  '/finance/reports/balance-sheet': FileText,
  '/finance/reports/profit-loss': TrendingUp,
  '/finance/reports/trial-balance': ClipboardList,
  '/finance/reports/general-ledger': BookOpen,
  '/finance/reports/aging/receivables': BarChart3,
  '/finance/reports/cash-position': Wallet,
  '/finance/reports/vat': Landmark,
  '/finance/transactions': ArrowRightLeft,
  '/finance/invoices': Receipt,
  '/finance/allocations': Link2,
  '/finance/receivables': HandCoins,
  '/finance/follow-ups': CalendarCheck,
  '/finance/schedule?view=todo': CalendarCheck,
  '/finance/schedule': CalendarDays,
  '/finance/action-items': ClipboardList,
  '/finance/payables': CreditCard,
  '/finance/cheques': Receipt,
  '/finance/masters/accounts': BookOpen,
  '/finance/masters/parties': UserCheck,
  '/finance/settings': Settings2,
  '/finance/settings/voucher-types': FileText,
  '/finance/settings/account-mapping': GitBranch,
  '/finance/settings/currencies': HandCoins,
  '/finance/settings/periods': Calendar,
  '/finance/settings/audit-log': History,
  '/finance/dashboard': LayoutDashboard,
}

function getIcon(href: string) {
  return ICON_MAP[href] ?? FileText
}

interface FinanceSidebarProps {
  className?: string
}

export function FinanceSidebar({ className }: FinanceSidebarProps) {
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const zone = detectZone(location.pathname)

  // Don't render for start zone (fullscreen mode)
  if (zone === 'start') {
    return null
  }

  const userPermissions = useUserPermissions()
  const navItems = filterZoneItemsByPermissions(
    ZONE_NAV_ITEMS[zone as Exclude<FinanceZone, 'start'>] ?? [],
    userPermissions
  )

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar',
        'transition-all duration-200 ease-in-out',
        sidebarCollapsed ? 'w-[72px]' : 'w-56',
        className
      )}
    >
      {/* Header with logo and collapse toggle */}
      <div
        className={cn(
          'flex h-14 items-center border-b',
          sidebarCollapsed ? 'justify-center px-3' : 'justify-between px-3'
        )}
      >
        <Link
          to="/finance"
          className={cn(
            'flex items-center gap-2',
            sidebarCollapsed && 'justify-center'
          )}
          aria-label="FreighAI home"
        >
          {sidebarCollapsed ? (
            <img src="/logo-icon-light.png" alt="FreighAI" className="h-8 w-8 rounded-md object-contain" />
          ) : (
            <>
              <img src="/logo-wordmark-dark.png" alt="FreighAI" className="h-7 w-auto object-contain block dark:hidden" />
              <img src="/logo-wordmark-light.png" alt="FreighAI" className="h-7 w-auto object-contain hidden dark:block" />
            </>
          )}
        </Link>

        {!sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleSidebar}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {/* Zone-specific navigation */}
        <div className="space-y-1">
          {(() => {
            // Group items by their group property
            const groups: { group: string | null; items: typeof navItems }[] = []
            let currentGroup: string | null = null

            navItems.forEach((item) => {
              const itemGroup = item.group ?? null
              if (groups.length === 0 || itemGroup !== currentGroup) {
                groups.push({ group: itemGroup, items: [] })
                currentGroup = itemGroup
              }
              groups[groups.length - 1].items.push(item)
            })

            return groups.map((group, groupIndex) => (
              <div key={group.group ?? 'ungrouped'} className={groupIndex > 0 ? 'mt-4' : ''}>
                {/* Group header */}
                {group.group && !sidebarCollapsed && (
                  <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {group.group}
                  </div>
                )}
                {group.group && sidebarCollapsed && (
                  <div className="mx-auto my-2 h-px w-6 bg-border" />
                )}
                {/* Group items */}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = getIcon(item.href)
                    const isActive =
                      location.pathname === item.href ||
                      (item.href !== '/finance/reports' &&
                        location.pathname.startsWith(item.href))

                    // Special case for "All Reports" - only active on exact match
                    const isReportsHub =
                      item.href === '/finance/reports' &&
                      location.pathname === '/finance/reports'

                    const active = isActive || isReportsHub

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                          active
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          sidebarCollapsed && 'justify-center px-2'
                        )}
                        title={sidebarCollapsed ? item.label : undefined}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))
          })()}
        </div>
      </nav>

      {/* Zone Switcher - Quick navigation between zones */}
      <div className="border-t px-3 py-2">
        {!sidebarCollapsed && (
          <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
            Switch to
          </div>
        )}
        <div className={cn(
          sidebarCollapsed ? 'flex flex-col items-center gap-1' : 'space-y-0.5'
        )}>
          {[
            { zone: 'entry', href: '/finance/transactions', icon: Zap, label: 'My Tasks' },
            { zone: 'queues', href: '/finance/receivables', icon: ClipboardList, label: 'Work Queues' },
            { zone: 'reports', href: '/finance/reports', icon: BarChart3, label: 'Reports' },
          ].filter(item => item.zone !== zone).map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.zone}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-1.5 text-xs transition-colors',
                  'text-muted-foreground/70 hover:text-foreground hover:bg-muted',
                  sidebarCollapsed && 'justify-center px-2'
                )}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            )
          })}
        </div>
      </div>

      {/* Footer - User Guide, Settings + Expand button */}
      <div className="border-t px-3 py-3 space-y-1">
        {/* User Guide link (opens in new tab) */}
        <a
          href="/finance/guide"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
            'text-muted-foreground hover:bg-muted hover:text-foreground',
            sidebarCollapsed && 'justify-center px-2'
          )}
          title={sidebarCollapsed ? 'User Guide' : undefined}
        >
          <HelpCircle className="h-4 w-4 shrink-0" />
          {!sidebarCollapsed && <span>User Guide</span>}
        </a>

        {/* Finance Settings link */}
        <Link
          to="/finance/settings"
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
            'text-muted-foreground hover:bg-muted hover:text-foreground',
            sidebarCollapsed && 'justify-center px-2'
          )}
          title={sidebarCollapsed ? 'Finance Settings' : undefined}
        >
          <Settings2 className="h-4 w-4 shrink-0" />
          {!sidebarCollapsed && <span>Finance Settings</span>}
        </Link>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-full mt-2"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </aside>
  )
}

/**
 * Mobile version of Finance Sidebar for Sheet overlay
 */
export function FinanceSidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const zone = detectZone(location.pathname)

  // For start zone, show minimal content
  if (zone === 'start') {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center border-b px-4">
          <Link
            to="/finance"
            className="flex items-center gap-2"
            onClick={onNavigate}
            aria-label="FreighAI home"
          >
            <img src="/logo-wordmark-dark.png" alt="FreighAI" className="h-7 w-auto object-contain block dark:hidden" />
            <img src="/logo-wordmark-light.png" alt="FreighAI" className="h-7 w-auto object-contain hidden dark:block" />
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-sm text-muted-foreground text-center">
            Use Lookup to navigate
          </p>
        </div>
      </div>
    )
  }

  const userPermissions = useUserPermissions()
  const navItems = filterZoneItemsByPermissions(
    ZONE_NAV_ITEMS[zone as Exclude<FinanceZone, 'start'>] ?? [],
    userPermissions
  )

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-14 items-center border-b px-4">
        <Link
          to="/finance"
          className="flex items-center gap-2"
          onClick={onNavigate}
          aria-label="FreighAI home"
        >
          <img src="/logo-wordmark-dark.png" alt="FreighAI" className="h-7 w-auto object-contain block dark:hidden" />
          <img src="/logo-wordmark-light.png" alt="FreighAI" className="h-7 w-auto object-contain hidden dark:block" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {/* Zone items */}
        <div className="space-y-1">
          {(() => {
            // Group items by their group property
            const groups: { group: string | null; items: typeof navItems }[] = []
            let currentGroup: string | null = null

            navItems.forEach((item) => {
              const itemGroup = item.group ?? null
              if (groups.length === 0 || itemGroup !== currentGroup) {
                groups.push({ group: itemGroup, items: [] })
                currentGroup = itemGroup
              }
              groups[groups.length - 1].items.push(item)
            })

            return groups.map((group, groupIndex) => (
              <div key={group.group ?? 'ungrouped'} className={groupIndex > 0 ? 'mt-4' : ''}>
                {/* Group header */}
                {group.group && (
                  <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {group.group}
                  </div>
                )}
                {/* Group items */}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = getIcon(item.href)
                    const isActive = location.pathname === item.href ||
                      (item.href !== '/finance/reports' && location.pathname.startsWith(item.href))

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={onNavigate}
                        className={cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))
          })()}
        </div>
      </nav>

      {/* Zone Switcher - Quick navigation between zones */}
      <div className="border-t px-3 py-2">
        <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
          Switch to
        </div>
        <div className="space-y-0.5">
          {[
            { zone: 'entry', href: '/finance/transactions', icon: Zap, label: 'My Tasks' },
            { zone: 'queues', href: '/finance/receivables', icon: ClipboardList, label: 'Work Queues' },
            { zone: 'reports', href: '/finance/reports', icon: BarChart3, label: 'Reports' },
          ].filter(item => item.zone !== zone).map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.zone}
                to={item.href}
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-lg px-3 py-1.5 text-xs text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Footer - User Guide + Settings */}
      <div className="border-t px-3 py-3 space-y-1">
        <a
          href="/finance/guide"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <HelpCircle className="h-4 w-4" />
          <span>User Guide</span>
        </a>
        <Link
          to="/finance/settings"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <Settings2 className="h-4 w-4" />
          <span>Finance Settings</span>
        </Link>
      </div>
    </div>
  )
}
